import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card,
  TablePagination, TextField, Box, Alert, Snackbar, Button, CircularProgress,
  IconButton, InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Checkbox
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { propService } from '../../services/propService';
import debounce from 'lodash/debounce';
import { EmptyState, PageHeader } from '../ui';

export const PropList: React.FC = () => {
  const [props, setProps] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedProps, setSelectedProps] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPropId, setEditingPropId] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    phoneVersion: '',
    propFile: '',
    propKey: '',
    propValue: '',
    note: '',
    status: '1'
  });

  const fetchProps = useCallback(async (currentPage: number, currentRowsPerPage: number, search?: string) => {
    setIsLoading(true);
    try {
      const response = await propService.getProps({
        limit: currentRowsPerPage,
        page: currentPage,
        searchTerm: search
      });
      setProps(response.props);
      setTotalCount(response.total || 0);
    } catch (error) {
      setError('Failed to fetch props');
      console.error('Error fetching props:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProps(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, fetchProps]);

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      setSearchTerm(searchValue);
    }, 500),
    [rowsPerPage, fetchProps]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    //fetchProps(0, newRowsPerPage, searchTerm);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    //fetchProps(newPage, rowsPerPage, searchTerm);
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvText = e.target?.result as string;
          const lines = csvText.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());

          // Validate required headers
          const requiredHeaders = ['phoneVersion', 'propFile', 'propKey', 'propValue', 'note', 'status'];
          const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));

          if (missingHeaders.length > 0) {
            setError(`Missing required columns: ${missingHeaders.join(', ')}`);
            setImportLoading(false);
            return;
          }

          // Parse CSV data
          const props = lines.slice(1).map(line => {
            const values = line.split(',').map(value => value.trim());
            const prop: any = {};
            headers.forEach((header, index) => {
              prop[header] = values[index] || '';
            });
            return {
              phoneVersion: prop.phoneVersion || '',
              propFile: prop.propFile || '',
              propKey: prop.propKey || '',
              propValue: prop.propValue || '',
              note: prop.note || '',
              status: prop.status || '1'
            };
          }).filter(prop => prop.phoneVersion && prop.propKey); // Filter out empty rows

          if (props.length === 0) {
            setError('No valid data found in the CSV file');
            setImportLoading(false);
            return;
          }

          const response = await propService.importProps(props);
          if (response.result) {
            setSuccessMessage('Props imported successfully');
            fetchProps(page, rowsPerPage, searchTerm);
          } else {
            setError(response.message || 'Failed to import props');
          }
        } catch (error) {
          console.error('Error processing file:', error);
          setError('Failed to process file. Please check the CSV format.');
        } finally {
          setImportLoading(false);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setError('Failed to read file');
      setImportLoading(false);
    }
  };

  const handleDelete = async (ids: string[]) => {
    try {
      const response = await propService.removeProps(ids);
      if (response.result) {
        setSuccessMessage('Props deleted successfully');
        fetchProps(page, rowsPerPage, searchTerm);
        setSelectedProps([]);
        setDeleteDialogOpen(false);
      } else {
        setError(response.message || 'Failed to delete props');
      }
    } catch (error) {
      console.error('Error deleting props:', error);
      setError('Failed to delete props');
    }
  };

  const handleSelectProp = (no: string) => {
    setSelectedProps(prev =>
      prev.includes(no)
        ? prev.filter(id => id !== no)
        : [...prev, no]
    );
  };

  const handleStartEdit = (prop: any) => {
    setEditingPropId(prop.no);
    setEditForm({
      phoneVersion: prop.phoneVersion || '',
      propFile: prop.propFile || '',
      propKey: prop.propKey || '',
      propValue: prop.propValue || '',
      note: prop.note || '',
      status: String(prop.status ?? '1')
    });
  };

  const handleCancelEdit = () => {
    setEditingPropId(null);
    setSavingEdit(false);
  };

  const handleSaveEdit = async () => {
    if (!editingPropId) return;
    if (!editForm.phoneVersion.trim() || !editForm.propKey.trim()) {
      setError('Phone Version và Prop Key là bắt buộc');
      return;
    }

    setSavingEdit(true);
    try {
      const response = await propService.updateProp(editingPropId, editForm);
      if (response.result) {
        setSuccessMessage('Prop updated successfully');
        setProps(prev => prev.map(item => (
          item.no === editingPropId ? { ...item, ...editForm, status: Number(editForm.status) } : item
        )));
        setEditingPropId(null);
      } else {
        setError(response.message || 'Failed to update prop');
      }
    } catch (error) {
      console.error('Error updating prop:', error);
      setError('Failed to update prop');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}>
        <Alert onClose={() => setSuccessMessage(null)} severity="success">{successMessage}</Alert>
      </Snackbar>
      <PageHeader
        eyebrow="Config"
        title="Build props"
        description="Per-device build properties applied when spoofing a phone profile."
        actions={
          <>
            {selectedProps.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isLoading || importLoading}
              >
                Delete {selectedProps.length} selected
              </Button>
            )}
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={isLoading || importLoading}
            >
              Import props
              <input type="file" hidden accept=".csv" onChange={handleFileImport} />
            </Button>
          </>
        }
      >
        <TextField
          sx={{ width: { xs: '100%', sm: 420 } }}
          placeholder="Search by phone version, prop key, value…"
          value={searchInput}
          onChange={handleSearchChange}
          disabled={isLoading || importLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </PageHeader>

      <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedProps.length > 0 && selectedProps.length < props.length}
                  checked={props.length > 0 && selectedProps.length === props.length}
                  onChange={() => {
                    if (selectedProps.length === props.length) {
                      setSelectedProps([]);
                    } else {
                      setSelectedProps(props.map(p => p.no));
                    }
                  }}
                />
              </TableCell>
              <TableCell>Phone Version</TableCell>
              <TableCell>Prop File</TableCell>
              <TableCell>Prop Key</TableCell>
              <TableCell>Prop Value</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || importLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : props.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ p: 0 }}>
                  <EmptyState
                    icon={<TerminalOutlinedIcon />}
                    title="No props found"
                    description={
                      searchTerm
                        ? 'No property matches that search.'
                        : 'Import a CSV to load build properties.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              props.map((prop) => (
                <TableRow key={prop.no} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProps.includes(prop.no)}
                      disabled={!!editingPropId}
                      onChange={(e) => {
                        handleSelectProp(prop.no);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.phoneVersion}
                        onChange={(e) => setEditForm(prev => ({ ...prev, phoneVersion: e.target.value }))}
                      />
                    ) : prop.phoneVersion}
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.propFile}
                        onChange={(e) => setEditForm(prev => ({ ...prev, propFile: e.target.value }))}
                      />
                    ) : prop.propFile}
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.propKey}
                        onChange={(e) => setEditForm(prev => ({ ...prev, propKey: e.target.value }))}
                      />
                    ) : <span className="mono">{prop.propKey}</span>}
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.propValue}
                        onChange={(e) => setEditForm(prev => ({ ...prev, propValue: e.target.value }))}
                      />
                    ) : <span className="mono">{prop.propValue}</span>}
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.note}
                        onChange={(e) => setEditForm(prev => ({ ...prev, note: e.target.value }))}
                      />
                    ) : prop.note}
                  </TableCell>
                  <TableCell>
                    {editingPropId === prop.no ? (
                      <TextField
                        size="small"
                        value={editForm.status}
                        onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      />
                    ) : prop.status}
                  </TableCell>
                  <TableCell align="right">
                    {editingPropId === prop.no ? (
                      <>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={handleSaveEdit}
                              disabled={savingEdit}
                            >
                              {savingEdit ? <CircularProgress size={18} /> : <SaveIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <span>
                            <IconButton onClick={handleCancelEdit} disabled={savingEdit}>
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handleStartEdit(prop)}
                            disabled={!!editingPropId || importLoading || isLoading}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
      />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedProps.length} selected prop(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleDelete(selectedProps)}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};